// pages/pace-calculator.tsx

import React, { useState } from "react";

import {
  TextField,
  MenuItem,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardHeader,
  CardContent,
  ListSubheader,
  Alert,
  CardActionArea,
  Button,
  CardActions,
  Box,
} from "@mui/material";

import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SyncIcon from "@mui/icons-material/Sync";
import { makeStyles } from "tss-react/mui";

import CallToAction from "../../components/CallToAction/CallToAction";
import RaceTimePredictor from "./RaceTimePredictor";
import TrainingPaces from "./TrainingPaces";

export type DistanceUnit = "km" | "miles" | "meters";
export type PaceUnit = "km" | "mile";
const formatTwoDigits = (value: number | string) => {
  const MAX = "59";
  value = value.toString();
  if (value.startsWith(MAX) && value.length > 2) {
    value = value.slice(2);
  }
  if (value.startsWith("0") && value.length > 1) {
    value = value.substring(1);
  }
  if (value.startsWith("0") && value.length > 1) {
    value = value.substring(1);
  }

  const end = value.length;
  const start = end - 2;
  value = value.slice(start, end);

  const numericValue = parseInt(value, 10);
  if (numericValue > 59) {
    value = MAX;
  }

  return value.padStart(2, "0");
};

export const calculatePace = (
  distance: string | number,
  hours: string,
  minutes: string,
  seconds: string,
  unit: DistanceUnit,
  paceUnit?: PaceUnit,
  logVerbose = false
) => {
  if (distance && (hours || minutes || seconds)) {
    const totalMinutes =
      Number(hours) * 60 + Number(minutes) + Number(seconds) / 60;
    let distanceInKm;

    if (unit === "km") {
      distanceInKm = Number(distance);
    } else if (unit === "miles") {
      distanceInKm = Number(distance) * 1.60934;
    } else if (unit === "meters") {
      distanceInKm = Number(distance) / 1000;
    } else if (unit === "yards") {
      distanceInKm = Number(distance) * 0.0009144;
    } else {
      distanceInKm = Number(distance); // default to km if unit is unrecognized
    }

    const calculatedPace = totalMinutes / distanceInKm;

    if (logVerbose) {
      /*  console.log({
        distance,
        unit,
        totalMinutes,
        distanceInKm,
        calculatedPace,
      }); */
    }
    let paceMinutesCalc = Math.floor(calculatedPace);
    let paceSecondsCalc = Math.round((calculatedPace - paceMinutesCalc) * 60);
    if (paceUnit && "km" !== paceUnit) {
      ({ minutes: paceMinutesCalc, seconds: paceSecondsCalc } = convertPace(
        paceMinutesCalc,
        paceSecondsCalc,
        "km",
        paceUnit
      ));
    }

    if (paceSecondsCalc === 60) {
      paceSecondsCalc = 0;
      ++paceMinutesCalc;
    }
    return { paceMinutesCalc, paceSecondsCalc };
  } else if (distance) {
    return { paceMinutesCalc: 0, paceSecondsCalc: 0 };
  }
};

export const distanceUnits = [
  { label: "km", value: "km" },
  { label: "miles", value: "miles" },
  { label: "meters", value: "meters" },
  // { label: "yards", value: "yards" },
];

export const eventDistances = [
  { label: "Half Marathon", value: { distance: 13.1, unit: "miles" } },
  { label: "Marathon", value: { distance: 26.2, unit: "miles" } },
  { label: "5K", value: { distance: 5, unit: "km" } },
  { label: "10K", value: { distance: 10, unit: "km" } },
  { label: "1 mile", value: { distance: 1, unit: "miles" } },
  { label: "5 miles", value: { distance: 5, unit: "miles" } },
  { label: "10 miles", value: { distance: 10, unit: "miles" } },
  { label: "800m", value: { distance: 800, unit: "meters" } },
  { label: "1500m", value: { distance: 1500, unit: "meters" } },
];

const size = "small";

export const calculatorFieldProps = { size, type: "number", fullWidth: true };

const useStyles = makeStyles()((theme) => ({
  toggleButton: {
    height: "40px",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  fullWidth: { width: "100%" },
}));
const convertPace = (
  minutes: number,
  seconds: number,
  from: PaceUnit,
  to: PaceUnit
) => {
  const totalSeconds = minutes * 60 + seconds;
  const conversionFactor = 1.60934;

  let convertedSeconds;

  if (from === "km" && to === "mile") {
    convertedSeconds = totalSeconds * conversionFactor;
  } else if (from === "mile" && to === "km") {
    convertedSeconds = totalSeconds / conversionFactor;
  } else {
    return { minutes, seconds }; // No conversion needed
  }

  return {
    minutes: Math.floor(convertedSeconds / 60),
    seconds: Math.round(convertedSeconds % 60),
  };
};

const PaceCalculator = () => {
  const { classes } = useStyles();
  const [distance, setDistance] = useState<string>("");
  const [distanceUnit, setDistanceUnit] = useState<"km" | "miles" | "meters">(
    "km"
  );
  const [hours, setHours] = useState<string>("00");
  const [minutes, setMinutes] = useState<string>("00");
  const [seconds, setSeconds] = useState<string>("00");
  const [paceMinutes, setPaceMinutes] = useState<string>("");
  const [paceSeconds, setPaceSeconds] = useState<string>("");
  const [paceUnit, setPaceUnit] = useState<"km" | "mile">("km");
  const [lastChanged, setLastChanged] = useState<
    "pace" | "time" | "distance" | null
  >(null);

  const handleReset = () => {
    setDistance("");
    setDistanceUnit("km");
    setHours("00");
    setMinutes("00");
    setSeconds("00");
    setPaceMinutes("");
    setPaceSeconds("");
    setLastChanged(null);
  };

  const handleDistanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDistance(event.target.value);
    updatePace(
      event.target.value,
      hours,
      minutes,
      seconds,
      distanceUnit,
      paceUnit
    );
    setLastChanged("distance");
  };

  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const newSeconds = seconds;
    const newMinutes = minutes;
    /* if (value === "-1") {
      const parsed = parseInt(newMinutes);
      if (parsed) {
        newMinutes = (parsed - 1).toString().padStart(2, "0");
        setMinutes(newMinutes);
      } else {
        const parsedSeconds = parseInt(newSeconds);
        if (parsedSeconds) {
          newSeconds = (parsedSeconds - 1).toString().padStart(2, "0");
          setSeconds(newSeconds);
          newMinutes = "59"
          setMinutes(newMinutes)
        }
      }
      value = "00";
    } */
    const newHours = formatTwoDigits(value);

    setHours(newHours);
    updatePace(
      distance,
      event.target.value,
      minutes,
      seconds,
      distanceUnit,
      paceUnit
    );
    setLastChanged("time");
  };

  const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;

    let newHours = hours;
    const newSeconds = seconds;
    if (value === "60") {
      value = "00";

      newHours = (parseInt(newHours) + 1).toString().padStart(2, "0");
      setHours(newHours);
    } else if (value === "-1") {
      const parsed = parseInt(newHours);
      if (parsed) {
        newHours = (parsed - 1).toString().padStart(2, "0");
        value = "59";
        setHours(newHours);
      } else {
        value = "00";
      }
    }
    const newMinutes = formatTwoDigits(value);
    setMinutes(newMinutes);

    updatePace(
      distance,
      newHours,
      newMinutes,
      newSeconds,
      distanceUnit,
      paceUnit
    );
    setLastChanged("time");
  };

  const handleSecondsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;
    let newMinutes = minutes;
    let newHours = hours;
    if (value === "60") {
      value = "00";
      if (newMinutes === "59") {
        newMinutes = "00";
        newHours = (parseInt(newHours) + 1).toString().padStart(2, "0");
        setHours(newHours);
      } else {
        newMinutes = (parseInt(newMinutes) + 1).toString().padStart(2, "0");
      }
      setMinutes(newMinutes);
    } else if (value === "-1") {
      const parsed = parseInt(newMinutes);
      if (parsed) {
        newMinutes = (parsed - 1).toString().padStart(2, "0");
        value = "59";
        setMinutes(newMinutes);
      } else {
        const parsedHours = parseInt(newHours);
        if (parsedHours) {
          newHours = (parsedHours - 1).toString().padStart(2, "0");
          setHours(newHours);
          newMinutes = "59";
          setMinutes(newMinutes);
          value = "59";
        } else {
          value = "00";
        }
      }
    }

    const newSeconds = formatTwoDigits(value);
    setSeconds(newSeconds);
    updatePace(
      distance,
      newHours,
      newMinutes,
      newSeconds,
      distanceUnit,
      paceUnit
    );
    setLastChanged("time");
  };

  const handlePaceMinutesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;

    const newPaceMinutes = formatTwoDigits(value);
    setPaceMinutes(newPaceMinutes);
    calculateTime(
      distance,
      newPaceMinutes,
      paceSeconds,
      distanceUnit,
      paceUnit
    );
    setLastChanged("pace");
  };

  const handlePaceSecondsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let { value } = event.target;

    let newPaceMinutes = paceMinutes || "00";
    if (value === "60") {
      newPaceMinutes = (parseInt(newPaceMinutes) + 1)
        .toString()
        .padStart(2, "0");
      value = "00";
      setPaceMinutes(newPaceMinutes);
    } else if (value === "-1") {
      const parsedMinutes = parseInt(newPaceMinutes);

      if (parsedMinutes) {
        newPaceMinutes = (parsedMinutes - 1).toString().padStart(2, "0");
        setPaceMinutes(newPaceMinutes);
        value = "59";
      } else {
        value = "00";
      }
    }

    const newPaceSeconds = formatTwoDigits(value);

    setPaceSeconds(newPaceSeconds);
    calculateTime(
      distance,
      newPaceMinutes,
      newPaceSeconds,
      distanceUnit,
      paceUnit
    );
    setLastChanged("pace");
  };

  const handlePaceUnitChange = (
    event: React.MouseEvent<HTMLElement>,
    newUnit: "km" | "mile"
  ) => {
    if (newUnit !== null && newUnit !== paceUnit) {
      const convertedPace = convertPace(
        Number(paceMinutes),
        Number(paceSeconds),
        paceUnit,
        newUnit
      );

      if (convertedPace.seconds === 60) {
        convertedPace.seconds = 0;
        ++convertedPace.minutes;
      }
      setPaceMinutes(formatTwoDigits(convertedPace.minutes));
      setPaceSeconds(formatTwoDigits(convertedPace.seconds));
      setPaceUnit(newUnit);
    }
  };

  const handleEventDistanceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedValue = event.target.value;
    const selectedEvent = eventDistances.find((e) => e.label === selectedValue);
    const newUnit = (
      selectedEvent ? selectedEvent.value.unit : selectedValue
    ) as "km" | "miles";
    const newDistance = selectedEvent ? selectedEvent.value.distance : distance;
    if (selectedEvent) {
      setDistance(newDistance);
    }

    setDistanceUnit(newUnit);
    updatePace(newDistance, hours, minutes, seconds, newUnit, paceUnit);
    setLastChanged("distance");
  };

  const updatePace = (
    distance: string | number,
    hours: string,
    minutes: string,
    seconds: string,
    unit: DistanceUnit,
    paceUnit: PaceUnit
  ) => {
    if (!distance) {
      return;
    }
    const { paceMinutesCalc, paceSecondsCalc } = calculatePace(
      distance,
      hours,
      minutes,
      seconds,
      unit,
      paceUnit,
      true
    );

    setPaceMinutes(formatTwoDigits(paceMinutesCalc));
    setPaceSeconds(formatTwoDigits(paceSecondsCalc));
  };

  const calculateTime = (
    distance: string | number,
    paceMinutes: string,
    paceSeconds: string,
    unit: DistanceUnit,
    paceUnit: PaceUnit
  ) => {
    if (distance && paceMinutes && paceSeconds) {
      distance = Number(distance);
      const paceInMinutes = Number(paceMinutes) + Number(paceSeconds) / 60;
      const normalisedPaceUnit = paceUnit === "mile" ? "miles" : paceUnit;
      const normalisedDistance =
        normalisedPaceUnit === unit
          ? distance
          : normalisedPaceUnit === "km"
          ? unit === "miles"
            ? distance * 1.60934
            : // must be meters
              distance * 1000
          : unit === "km"
          ? // Pace unit must be miles
            distance * 0.621371
          : (distance * 0.621371) / 1000;

      const totalMinutes = paceInMinutes * normalisedDistance;
      const totalHours = Math.floor(totalMinutes / 60);
      const totalMinutesRest = Math.floor(totalMinutes % 60);
      const totalSecondsRest = Math.round((totalMinutes % 1) * 60);
      setHours(formatTwoDigits(totalHours));
      setMinutes(formatTwoDigits(totalMinutesRest));
      setSeconds(formatTwoDigits(totalSecondsRest));
    }
  };

  const labelProps = {
    item: true,
    xxs: 12,
    xs: 3,
    sx: { alignContent: "center", fontSize: "0.75rem" },
  };

  const showResult = !!(
    parseInt(distance || "") &&
    (parseInt(hours) || parseInt(minutes) || parseInt(seconds))
  );
  return (
    <Grid container spacing={2} sx={{ marginTop: 0 }}>
      <Grid item md={6} className={classes.fullWidth}>
        <Card>
          <CardHeader
            title="Pace calculator"
            subheader="Specify distance/event and average pace to get overall time or vice-versa"
          ></CardHeader>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item xs={12} className={classes.fullWidth}>
                    <Grid container spacing={2}>
                      <Grid {...labelProps}>Distance</Grid>
                      <Grid item xxs={6} xs={3}>
                        <TextField
                          {...calculatorFieldProps}
                          label="Distance"
                          autoFocus
                          value={distance}
                          onChange={handleDistanceChange}
                          inputProps={{ min: 0 }}
                        />
                      </Grid>
                      <Grid item xxs={6} xs={6}>
                        <TextField
                          select
                          {...calculatorFieldProps}
                          label="Unit/Event"
                          value={distanceUnit}
                          onChange={handleEventDistanceChange}
                        >
                          <ListSubheader>Unit</ListSubheader>
                          {distanceUnits.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                          <ListSubheader>Event</ListSubheader>
                          {eventDistances.map((option) => (
                            <MenuItem key={option.label} value={option.label}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/*   <Grid item xs={12}>
                    <Divider variant="middle" />
                  </Grid> */}

                  <Grid item xs={12} className={classes.fullWidth}>
                    <Grid container spacing={2}>
                      <Grid {...labelProps}>Time</Grid>
                      <Grid item xxs={4} xs={3}>
                        <TextField
                          {...calculatorFieldProps}
                          label="Hours"
                          value={hours}
                          inputProps={{ min: 0 }}
                          onChange={handleHoursChange}
                        />
                      </Grid>
                      <Grid item xxs={4} xs={3}>
                        <TextField
                          {...calculatorFieldProps}
                          label="Minutes"
                          inputProps={{ max: 60, min: -1 }}
                          value={minutes}
                          onChange={handleMinutesChange}
                        />
                      </Grid>
                      <Grid item xxs={4} xs={3}>
                        <TextField
                          {...calculatorFieldProps}
                          label="Seconds"
                          value={seconds}
                          inputProps={{ max: 60, min: -1 }}
                          onChange={handleSecondsChange}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* <Grid item xs={12}>
                    <Divider variant="middle" textAlign="left">
                      Or
                    </Divider>
                  </Grid> */}

                  <Grid item xs={12} className={classes.fullWidth}>
                    <Grid container spacing={2}>
                      <Grid {...labelProps}>Pace</Grid>
                      <Grid item xxs={4} xs={3}>
                        <TextField
                          {...calculatorFieldProps}
                          label="Minutes"
                          value={paceMinutes}
                          inputProps={{ max: 59, min: 0 }}
                          onChange={handlePaceMinutesChange}
                        />
                      </Grid>
                      <Grid item xxs={4} xs={3}>
                        <TextField
                          {...calculatorFieldProps}
                          label="Seconds"
                          value={paceSeconds}
                          inputProps={{ max: 60, min: -1 }}
                          onChange={handlePaceSecondsChange}
                        />
                      </Grid>
                      <Grid item xxs={4} xs={3} sx={{ alignContent: "center" }}>
                        <ToggleButtonGroup
                          size={size}
                          value={paceUnit}
                          className={classes.toggleButton}
                          exclusive
                          onChange={handlePaceUnitChange}
                          aria-label="pace unit"
                          fullWidth
                        >
                          <ToggleButton value="km" aria-label="per km">
                            km
                          </ToggleButton>
                          <ToggleButton value="mile" aria-label="per mile">
                            mile
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions sx={{ minHeight: "120px" }}>
            <Grid container spacing={2} sx={{ marginTop: "auto" }}>
              <Grid {...labelProps} sx={{ marginTop: "auto" }}>
                <Button
                  startIcon={<RestartAltIcon />}
                  color="primary"
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item xs={9}>
                {showResult ? (
                  <>
                    <Alert sx={{ marginTop: 1 }}>
                      <Typography variant="body1">
                        {lastChanged === "pace" ? (
                          <>
                            At{" "}
                            <strong>
                              {paceMinutes}:{paceSeconds} per {paceUnit}
                            </strong>{" "}
                            pace, you would cover{" "}
                            <strong>
                              {distance} {distanceUnit}
                            </strong>{" "}
                            in{" "}
                            <strong>
                              {hours}:{minutes}:{seconds}
                            </strong>
                            .
                          </>
                        ) : (
                          <>
                            To cover{" "}
                            <strong>
                              {distance} {distanceUnit}
                            </strong>{" "}
                            in{" "}
                            <strong>
                              {hours}:{minutes}:{seconds}
                            </strong>
                            , you would need to run at{" "}
                            <strong>
                              {paceMinutes}:{paceSeconds} per {paceUnit}
                            </strong>{" "}
                            pace.
                          </>
                        )}
                      </Typography>
                    </Alert>
                  </>
                ) : (
                  <div></div>
                )}
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </Grid>
      <Grid item md={6} className={classes.fullWidth}>
        <Card sx={{ height: "100%" }}>
          <CardHeader
            title="Training pace recommendations"
            subheader={
              !showResult ? (
                "Enter target race time/pace for training pace suggestions."
              ) : (
                <>
                  {"If you're training to run "}
                  <strong>
                    {distance} {distanceUnit}
                  </strong>{" "}
                  in{" "}
                  <strong>
                    {hours}:{minutes}:{seconds}
                  </strong>
                  , below are the recommended paces to train at.
                </>
              )
            }
          ></CardHeader>
          <CardContent>
            {showResult ? (
              <TrainingPaces
                distanceUnit={distanceUnit}
                distance={distance}
                hours={hours}
                minutes={minutes}
                seconds={seconds}
                paceUnit={paceUnit}
              />
            ) : (
              <Alert severity="info">
                Set distance and pace or time for recommendations
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item className={classes.fullWidth}>
        <Card>
          <CardHeader
            title="Race time predictions"
            subheader={
              !showResult ? (
                "Your predicted race performances if you are able to run the distance/pace/time set above."
              ) : (
                <>
                  If you can run{" "}
                  <strong>
                    {distance} {distanceUnit}
                  </strong>{" "}
                  in{" "}
                  <strong>
                    {hours}:{minutes}:{seconds}
                  </strong>
                  , below are the results you could expect to achieve with
                  proper training and equivalent conditions.
                </>
              )
            }
          ></CardHeader>
          <CardContent>
            {showResult ? (
              <RaceTimePredictor
                distance={distance}
                distanceUnit={distanceUnit}
                paceUnit={paceUnit}
                hours={hours}
                minutes={minutes}
                seconds={seconds}
              />
            ) : (
              <Alert severity="info">
                Set distance and pace or time for predictions
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item sx={{ width: "100%" }}>
        <CallToAction
          callToAction="syncGear"
          //description={description}
          icon={<SyncIcon sx={{ fontSize: { sm: "3rem", md: "4rem" } }} />}
        />
      </Grid>
    </Grid>
  );
};

export default PaceCalculator;
